import { useContext, useCallback, useState, useEffect } from 'react';
import _get from 'lodash/get';
import _omit from 'lodash/omit';
import _isNil from 'lodash/isNil';
import _find from 'lodash/find';
import dayjs from 'dayjs';

import { AjaxContext, AppContext, EMPTY_ARRAY, formatListData, UserContext } from 'commons-ui';
import { ACTION, PAGE_NAMES, USER_PROFILE_RESOURCES } from '../../../constant';
import { UserDetailsContext } from '../../../context/userDetails';
import { EURL } from '../../../enum';

import {
	VIEW_FIELD_KEYS,
	USER_STATUS_MAP,
	USER_FRAUD_STATUS_MAP,
	USER_STATUS_LABEL,
	USER_FRAUD_STATUS_LABEL,
	USER_FRAUD_STATUSES,
	COLUMNS_NAME_BANK_DETAILS,
	COLUMNS_NAME_UPI_DETAILS,
	DETAIL_TYPE_BANK,
	DETAIL_TYPE_UPI
} from './constants';
import {
	DownloadPayoutDetailsParams,
	BankDetailItem,
	UpiDetailItem,
	PayoutDetailsApiResponse
} from '../../../models/basicDetails.model';

const useService = () => {
	const [blockUnblockDetails, setBlockUnblockDetails] = useState([]);
	const [payoutDetailsIsCollapsed, setPayoutDetailsIsCollapsed] = useState<boolean>(true);
	const [bankDetailsIsCollapsed, setBankDetailsIsCollapsed] = useState<boolean>(true);
	const [upiDetailsIsCollapsed, setUpiDetailsIsCollapsed] = useState<boolean>(true);
	const [dataFetchComplete, setDataFetchComplete] = useState<boolean>(false);
	const [bankAndUpiDetails, setBankAndUpiDetails] = useState<any[]>([]);
	const [downloadLoading, setDownloadLoading] = useState(false);
	const [downloadError, setDownloadError] = useState<string | null>(null);
	const {
		app: { appName }
	} = useContext(AppContext);
	const { postData, getData } = useContext(AjaxContext);
	useContext(UserContext);
	const {
		selectedUser: { uid }
	} = useContext(UserDetailsContext);
	const { permissionsConfig } = useContext(UserContext);
	const getBasicUserInfo = useCallback(async () => {
		const permission = _find(permissionsConfig, { actionName: ACTION.getBasicUserDetails });
		const result = await postData({
			endpoint: `/dashboard/getBasicUserDetails/${PAGE_NAMES.userProfile}-${USER_PROFILE_RESOURCES.basicDetails}-getBasicUserDetails`,
			queryParams: {
				appName
			},
			body: { uid },
			customHeaders: {
				isMasked: permission?.isMasked || false
			}
		});
		const data = _get(result, ['data', 'data'], {});

		let fullName = _get(data, VIEW_FIELD_KEYS.FULL_NAME);
		const firstName = _get(data, VIEW_FIELD_KEYS.FIRST_NAME);
		const lastName = _get(data, VIEW_FIELD_KEYS.LAST_NAME);

		if (_isNil(fullName)) {
			if (!_isNil(firstName) && !_isNil(lastName)) {
				fullName = `${firstName} ${lastName}`;
			} else {
				fullName = firstName || lastName;
			}
		}

		data[VIEW_FIELD_KEYS.SIGNUP_TIMESTAMP] = dayjs(new Date(_get(data, VIEW_FIELD_KEYS.SIGNUP_TIMESTAMP))).format(
			'YYYY-MM-DD hh:mm A'
		);
		data[VIEW_FIELD_KEYS.USER_STATUS] = _get(USER_STATUS_LABEL, _get(USER_STATUS_MAP, data[VIEW_FIELD_KEYS.USER_STATUS]));
		data[VIEW_FIELD_KEYS.USER_FRAUD_STATUS] = _get(
			USER_FRAUD_STATUS_LABEL,
			_get(USER_FRAUD_STATUS_MAP, _get(data, ['flags', 'isFraud'])),
			USER_FRAUD_STATUS_LABEL[USER_FRAUD_STATUSES.FAIR]
		);
		const rawIsMinor = _get(data, VIEW_FIELD_KEYS.IS_MINOR);
		let isMinorDisplay: string | undefined;
		if (!_isNil(rawIsMinor)) {
			isMinorDisplay = rawIsMinor ? 'Yes' : 'No';
		}

		data[VIEW_FIELD_KEYS.KYC_SUMMARY] = {
			[VIEW_FIELD_KEYS.KYC_STATUS]: _get(data, VIEW_FIELD_KEYS.KYC_STATUS),
			[VIEW_FIELD_KEYS.FULL_NAME]: fullName,
			[VIEW_FIELD_KEYS.DOB]: _get(data, VIEW_FIELD_KEYS.DOB),
			[VIEW_FIELD_KEYS.IS_MINOR]: isMinorDisplay
		};
		setBlockUnblockDetails(data.blockHistory);

		return _omit(data, [VIEW_FIELD_KEYS.KYC_STATUS, VIEW_FIELD_KEYS.FULL_NAME, VIEW_FIELD_KEYS.DOB, VIEW_FIELD_KEYS.IS_MINOR]);
	}, []);

	const getWalletDetails = useCallback(async () => {
		const permission = _find(permissionsConfig, { actionName: ACTION.getWalletDetails });
		const result = await postData({
			endpoint: `/dashboard/getWalletDetails/${PAGE_NAMES.userProfile}-${USER_PROFILE_RESOURCES.transactionHistory}-getWalletDetails`,
			queryParams: {
				appName
			},
			body: { uid },
			customHeaders: {
				isMasked: permission?.isMasked || false
			}
		});
		const data = _get(result, ['data', 'data'], {});
		const { addedCoins = 0, unlockedCoins = 0, actualCoins = 0, bonusCoins = 0 } = data;
		const cashbackCoins = data.cashbackCoins || 0;
		return {
			totalBalance: ((addedCoins + unlockedCoins + actualCoins + bonusCoins + cashbackCoins) / 100).toFixed(2),
			depositMoney: (addedCoins / 100).toFixed(2),
			winnings: (actualCoins / 100).toFixed(2),
			bonus: ((unlockedCoins + bonusCoins) / 100).toFixed(2),
			cashback: (cashbackCoins / 100).toFixed(2)
		};
	}, []);

	const getBlockUnblockDetails = async () => {
		const listData = await formatListData(blockUnblockDetails);
		return { rowCount: listData.length, data: listData };
	};

	const getUpiAndBankDetails = useCallback(async () => {
		const permission = _find(permissionsConfig, { actionName: ACTION.getBankDetails });
		const result = await postData({
			endpoint: `/dashboard/getBankDetails/${PAGE_NAMES.userProfile}-${USER_PROFILE_RESOURCES.basicDetails}-getBankDetails`,
			queryParams: {
				appName
			},
			body: { uid },
			customHeaders: {
				isMasked: permission?.isMasked || false
			}
		});

		const bankDetails: any = _get(result, ['data', 'data', 'bankDetailsList'], EMPTY_ARRAY);
		const upiDetails = _get(result, ['data', 'data', 'upiDetailsList'], EMPTY_ARRAY);

		setBankAndUpiDetails([bankDetails, upiDetails]);
		setDataFetchComplete(true);
	}, []);

	useEffect(() => {
		if (!dataFetchComplete && !payoutDetailsIsCollapsed) {
			getUpiAndBankDetails();
		}
		return () => {
			setBankAndUpiDetails([]);
		};
	}, [payoutDetailsIsCollapsed]);

	const fetchBankDetails = async () => {
		const listData = await formatListData(bankAndUpiDetails[0]);
		return { rowCount: listData.length, data: listData };
	};

	const fetchUpiDetails = async () => {
		const listData = await formatListData(bankAndUpiDetails[1]);
		return { rowCount: listData.length, data: listData };
	};

	const downloadBankDetailsCsvFile = (event: any) => {
		event.preventDefault();
		event.stopPropagation();
		const csvString = [
			COLUMNS_NAME_BANK_DETAILS,
			...(bankAndUpiDetails[0] || EMPTY_ARRAY).map((item: any) => [
				item.accountHolderName,
				item.accountNumber,
				item.validatedAccountHolderName,
				item.ifscCode,
				item.validationSource,
				item.isValid,
				item.isActive,
				item.deactivationReason,
				dayjs(new Date(Number(item.createdAt))).format('YYYY-MM-DD hh:mm A'),
				dayjs(new Date(Number(item.updatedAt))).format('YYYY-MM-DD hh:mm A')
			])
		]
			.map((e) => e.join(','))
			.join('\n');
		const csvContent = `data:text/csv;charset=utf-8, ${csvString}`;
		const encodedUri = encodeURI(csvContent);

		const link = document.createElement('a');
		link.setAttribute('href', encodedUri);
		link.setAttribute('download', `bankDetails_${uid}.csv`);
		document.body.appendChild(link);
		link.click();
	};

	const downloadUpiDetailsCsvFile = (event: any) => {
		event.preventDefault();
		event.stopPropagation();
		const csvString = [
			COLUMNS_NAME_UPI_DETAILS,
			...(bankAndUpiDetails[1] || EMPTY_ARRAY).map((item: any) => [
				item.vpaName,
				item.upiId,
				item.validationSource,
				item.isValid,
				item.isActive,
				item.deactivationReason,
				dayjs(new Date(Number(item.createdAt))).format('YYYY-MM-DD hh:mm A'),
				dayjs(new Date(Number(item.updatedAt))).format('YYYY-MM-DD hh:mm A')
			])
		]
			.map((e) => e.join(','))
			.join('\n');
		const csvContent = `data:text/csv;charset=utf-8, ${csvString}`;
		const encodedUri = encodeURI(csvContent);

		const link = document.createElement('a');
		link.setAttribute('href', encodedUri);
		link.setAttribute('download', `upiDetails_${uid}.csv`);
		document.body.appendChild(link);
		link.click();
	};

	const getTdsDetails = useCallback(async () => {
		const permission = _find(permissionsConfig, { actionName: ACTION.getTdsDetails });
		const result = await postData({
			endpoint: `/dashboard/getTdsDetails/${PAGE_NAMES.userProfile}-${USER_PROFILE_RESOURCES.basicDetails}-getTdsDetails`,
			queryParams: {
				appName
			},
			body: { uid },
			customHeaders: {
				isMasked: permission?.isMasked || false
			}
		});
		const data = _get(result, ['data', 'data'], {});
		const { outstandingTds = 0 } = data;
		return {
			tds: outstandingTds.toFixed(2)
		};
	}, []);

	const getUserPayoutDetails = async ({ userId, section, ticketId, description }: DownloadPayoutDetailsParams) => {
		const queryParams: { appName: string; userId: string; section: string; ticketId: string; description?: string } = {
			appName,
			userId: String(userId),
			section,
			ticketId
		};
		if (description) {
			queryParams.description = description;
		}

		const permission = _find(permissionsConfig, { actionName: ACTION.downloadUserPayoutDetails });
		return getData({
			endpoint: EURL.DOWNLOAD_USER_PAYOUT_DETAILS,
			queryParams,
			customHeaders: {
				isMasked: permission?.isMasked || false
			}
		});
	};

	const generateCsvStringForPayoutDetails = (apiData: PayoutDetailsApiResponse, section: string): string => {
		let csvString: string = '';
		if (section === DETAIL_TYPE_BANK) {
			const rows: string[][] = (apiData.bankDetailsList || []).map((item: BankDetailItem) => [
				item.accountHolderName,
				item.accountNumber,
				item.validatedAccountHolderName && item.validatedAccountHolderName !== 'null' ? item.validatedAccountHolderName : '',
				item.ifscCode,
				item.validationSource || '',
				String(item.isValid),
				item.isActive !== null ? String(item.isActive) : '',
				item.deactivationReason || '',
				dayjs(new Date(item.createdAt)).format('YYYY-MM-DD hh:mm A'),
				dayjs(new Date(item.updatedAt)).format('YYYY-MM-DD hh:mm A')
			]);
			csvString = [COLUMNS_NAME_BANK_DETAILS, ...rows].map((e) => e.join(',')).join('\n');
		} else if (section === DETAIL_TYPE_UPI) {
			const rows: string[][] = (apiData.upiDetailsList || []).map((item: UpiDetailItem) => [
				item.vpaName,
				item.upiId,
				item.validationSource || '',
				String(item.isValid),
				String(item.isActive),
				item.deactivationReason || '',
				dayjs(new Date(item.createdAt)).format('YYYY-MM-DD hh:mm A'),
				dayjs(new Date(item.updatedAt)).format('YYYY-MM-DD hh:mm A')
			]);
			csvString = [COLUMNS_NAME_UPI_DETAILS, ...rows].map((e) => e.join(',')).join('\n');
		}
		return csvString;
	};

	const downloadUserPayoutDetails = async ({ userId, section, ticketId, description }: DownloadPayoutDetailsParams) => {
		setDownloadLoading(true);
		setDownloadError(null);
		try {
			const result = await getUserPayoutDetails({ userId, section, ticketId, description });

			if (!result || result.success === false || !result.data || !result.data.data) {
				setDownloadError('Failed to download details. Please check your permissions or try again.');
				return;
			}
			const apiResponseData: PayoutDetailsApiResponse = result.data.data;
			const csvString: string = generateCsvStringForPayoutDetails(apiResponseData, section);

			const csvContent = `data:text/csv;charset=utf-8,${csvString}`;
			const encodedUri = encodeURI(csvContent);
			const link = document.createElement('a');
			link.setAttribute('href', encodedUri);
			link.setAttribute('download', `${section}_${userId}.csv`);
			document.body.appendChild(link);
			link.click();
			document.body.removeChild(link);
		} finally {
			setDownloadLoading(false);
		}
	};

	const viewDobDetails = async ({ ticketId, description }: { ticketId: string; description?: string }) => {
		return postData({
			endpoint: EURL.VIEW_DOB,
			body: {
				uid,
				ticketId,
				description
			}
		});
	};

	const viewPhoneDetails = async ({ ticketId, description }: { ticketId: string; description?: string }) => {
		return postData({
			endpoint: EURL.VIEW_PHONE,
			body: {
				uid,
				ticketId,
				description
			}
		});
	};

	const viewAlternatePhoneDetails = async ({ ticketId, description }: { ticketId: string; description?: string }) => {
		return postData({
			endpoint: EURL.VIEW_ALTERNATE_PHONE,
			body: {
				zupeeUserId: uid?.toString(),
				ticketId,
				description,
				section: 'phone_details'
			}
		});
	};

	const viewEmailDetails = async ({ ticketId, description }: { ticketId: string; description?: string }) => {
		return postData({
			endpoint: EURL.VIEW_EMAIL,
			body: {
				uid,
				ticketId,
				description
			}
		});
	};

	return {
		getBasicUserInfo,
		getWalletDetails,
		getBlockUnblockDetails,
		payoutDetailsIsCollapsed,
		setPayoutDetailsIsCollapsed,
		bankDetailsIsCollapsed,
		setBankDetailsIsCollapsed,
		upiDetailsIsCollapsed,
		setUpiDetailsIsCollapsed,
		dataFetchComplete,
		bankAndUpiDetails,
		fetchBankDetails,
		fetchUpiDetails,
		downloadBankDetailsCsvFile,
		downloadUpiDetailsCsvFile,
		getTdsDetails,
		getUpiAndBankDetails,
		downloadUserPayoutDetails,
		downloadLoading,
		downloadError,
		setDownloadError,
		viewDobDetails,
		viewPhoneDetails,
		viewEmailDetails,
		viewAlternatePhoneDetails
	};
};

export default useService;
